import { useSelector } from "react-redux";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import sanitize from "helpers/sanitize";
import { BalancedScorecardFieldValue } from "components/BalanceScorecard/BalancedScorecardFieldValue";
import { empty, formatFixedDigits, formatMoney, formatNumberTo, isTrue, removeTrailingZeros } from "helpers/common";
import { useMemo } from "react";
import { getGeneralSettings } from "store/generalSettingsSlice";
import TextTruncate from "components/ui/TextTruncate";
import { useTranslation } from "translation/useTranslation";
import Link from "components/ui/Link";
import { getModule } from "store/userSlice";
import { isDbModule } from "helpers/module";
import { translateGenderValue } from "./inputs/genderOptions";
import { employeeManagementPath } from "helpers/employeeDeeplink";

export default function EmployeeTableColumn({ field, employee, sortCols = null, forEmployeeManagement = false, displayLkTag = false, balancedScorecards = [], useInterviewIcon = false, resizedCount = null, linkToEmployeeManagement = false, missingPayfactorValues = false, missingPayfactorTooltip = '', useCalculationPayFactor = false }) {

  const { translate } = useTranslation();

  const settings = useSelector(getGeneralSettings);

  const currentModule = useSelector(getModule);

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const softwareSettings = useMemo(() => {
    const obj = {};

    if (settings) {
      settings.forEach(setting => {
        obj[setting.key] = setting.value;
      });
    }

    return obj;
  }, [settings]);

  const filteredBalancedScorecards = useMemo(() => {
    if (forEmployeeManagement && employee.excluded_from_evaluation) return [];

    if (balancedScorecards.length === 0) return employee.balanced_scorecards.data;
    else return balancedScorecards;
  }, [balancedScorecards, forEmployeeManagement]);

  const getLkVal = (obj, key) => {
    const ref = {
      'balanced_scorecard_template': 'balanced_scorecard_setting',
      'budget': 'budget_setting',
      'group': 'group'
    };

    if (['balanced_scorecard_template', 'budget', 'group'].includes(key)) {
      return obj[ref[key]]?.name ?? '';
    } else if (['lk', 'personal_interviews'].includes(key)) {
      return +obj[key + '_score'];
    } else if (key === 'trend') {
      let col = '';
      if (softwareSettings?.evaluation_trend_display_type === 'average_value') {
        col = 'trend_average_score';
      } else if (softwareSettings?.evaluation_trend_display_type === 'deviation') {
        col = 'trend_deviation';
      }

      return empty(obj[col]) ? -1 : parseFloat(formatNumberTo(obj[col], '.'));
    } else if (['pay_factor', 'result', 'fixed_payout'].includes(key)) {
      const value = useCalculationPayFactor && key === 'pay_factor' ? (obj?.calculation_pay_factor ?? obj[key]) : obj[key];
      return empty(value) ? -1 : parseFloat(formatNumberTo(value, '.'));
    }
  };

  const sortedEmployeeBalancedScorecards = useMemo(() => {
    if (!field.is_balanced_scorecard_specific) return [];

    if (!sortCols) return filteredBalancedScorecards.filter(bs => bs);

    const filteredSortCols = sortCols.filter(sc => sc.length === 3 && sc[2] === true);

    if (filteredSortCols.length === 0) return filteredBalancedScorecards.filter(bs => bs);

    const newFilteredLks = [...filteredBalancedScorecards].filter(bs => bs);

    const key = filteredSortCols[0][0];
    const direction = filteredSortCols[0][1];

    if (key === 'trend' && softwareSettings?.evaluation_trend_calculation_method === 'no_calculation') return newFilteredLks;

    newFilteredLks.sort((a, b) => {
      const valA = getLkVal(a, key);
      const valB = getLkVal(b, key);

      if (typeof valA === 'string') {
        return direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      } else if (typeof valA === 'number') {
        return direction === 'asc' ? valA - valB : valB - valA;
      }

      return 0;
    });
    return newFilteredLks;
  }, [filteredBalancedScorecards, sortCols]);

  const getEmployeeInformationMeta = (identifier) => {
    return employee?.employeeInformation?.data?.find((item) => item?.key === identifier) || null;
  };

  if (!field.is_balanced_scorecard_specific) {
    let val = '';
    let className = (field.alignment ? 'text-' + field.alignment : '');

    if (employee.hasOwnProperty(field.slug) && employee[field.slug] !== undefined && employee[field.slug] !== null) {
      val = employee[field.slug];
    } else if (employee?.information && employee.information.hasOwnProperty(field.name) && employee.information[field.name] !== undefined && employee.information[field.name] !== null) {
      val = employee.information[field.name];
    } else if (employee?.information && employee.information.hasOwnProperty(field.slug) && employee.information[field.slug] !== undefined && employee.information[field.slug] !== null) {
      val = employee.information[field.slug];
    } else if (employee?.employeeInformation && typeof employee.employeeInformation === 'object' && employee.employeeInformation.hasOwnProperty(field.name)) {
      val = employee.employeeInformation[field.name]['value'];
    }

    if (!empty(val) && field.slug === 'pay_grade') {
      val = employee?.pay_grade_label || val;
    } else if (!empty(val) && field.slug === 'job_differs_from_classification') {
      const resolvedLabel = getEmployeeInformationMeta(field.slug)?.resolved_label;
      if (resolvedLabel) val = resolvedLabel;
    }

    if (field.slug === 'last_name') {
      let exampleEmployeeIcon = '';

      if (employee.is_example) {
        exampleEmployeeIcon = '<i class="bi bi-flag ml-1" data-tooltip-id="example-employee-tooltip"></i>';
      }

      return <td className={className}>
        <div className="d-flex align-items-center">
          {
            linkToEmployeeManagement ?
              <a data-testid="employee-link" href={employee?.employee_number ? employeeManagementPath({ staffId: employee.employee_number }) : `/employee-management?search=${encodeURIComponent(employee.first_name + ' ' + val)}`} target="_blank" rel="noopener noreferrer">
                <TextTruncate text={val} resizedCount={resizedCount} />
              </a>
              :
              <TextTruncate text={val} resizedCount={resizedCount} />
          }
          <span className="v-middle" dangerouslySetInnerHTML={{ __html: sanitize(exampleEmployeeIcon) }}></span>
        </div>
      </td>
    } else if (field.slug === 'info') {
      return <td className={className}>
        {
          isTrue(employee?.information?.equal_opportunities_severe_disability_checkbox || false) && <i className="bi bi-shield-shaded text-default" data-tooltip-id="state-severe-disability-equality-tooltip"></i>
        }
      </td>
    }

    if (val === true) val = translate('form.yes', 'Ja');
    else if (val === false) val = translate('form.no', 'nein');
    else if (field.slug === 'gender') {
      val = translateGenderValue(val, translate);
    } else if (field.slug === 'working_hours') {
      if (isDbModuleActive && softwareSettings?.working_hours_display === 'parttime_fulltime' && val) {
        if (parseFloat(formatNumberTo(val, '.')) >= 100) {
          val = translate('general_settings.balanced_scorecard.working_hours_display.fulltime');
        } else {
          val = translate('general_settings.balanced_scorecard.working_hours_display.parttime');
        }
      } else if (val) {
        val = formatFixedDigits(val, field.decimal, field.fill_up) + (softwareSettings?.working_hours_type === 'hours' ? ' ' + translate('general_settings.working_hours_type.hours') : translate('general_settings.working_hours_type.percentage'));
      }
    } else if (['annual_compensation', 'individual_ceiling'].includes(field.slug)) {
      val = (val ? '€ ' + formatMoney(val) : '');
    } else if (['individual_pay_factor', 'individual_average_rating'].includes(field.slug)) {
      val = (val ? formatFixedDigits(val, field.decimal, field.fill_up) : '');
    } else if (['missing_months', 'missing_days'].includes(field.slug)) {
      val = (!empty(val) ? formatFixedDigits(val, field.decimal, field.fill_up) : '');
    } else if (field.slug === 'working_days_per_week') {
      val = (val ? formatFixedDigits(val, field.decimal, field.fill_up) : '');
    } else if (field.slug === 'scope_of_work' && !empty(val)) {
      val = val + '%';
    } else if (field.slug === 'employee_group') {
      val = employee?.employee_group?.name;
    }

    const showMissingPayfactorWarning = field.slug === 'pay_grade' && !isDbModuleActive && missingPayfactorValues;
    const missingPayfactorTooltipId = `employee-table-payfactor-missing-${employee?.hash_id}`;

    return <td className={className}>
      {
        showMissingPayfactorWarning ? <div className="d-flex align-items-center">
          <TextTruncate text={val} resizedCount={resizedCount} />
          <i className="bi bi-exclamation-triangle-fill text-warning cursor-pointer ml-2" data-tooltip-id={missingPayfactorTooltipId}></i>
          <ReactTooltip id={missingPayfactorTooltipId} place="bottom" clickable>
            {missingPayfactorTooltip}
          </ReactTooltip>
        </div> : <TextTruncate text={val} resizedCount={resizedCount} />
      }
    </td>
  } else {
    let classNames = (field.alignment ? 'text-' + field.alignment : '');

    if (field.slug === 'personal_interviews') {
      classNames += ' icons-cell';
    }

    return <td className={classNames}>
      {
        field.slug === 'lk' && employee.excluded_from_evaluation && <div>
          {
            <span>
              <i className={`bi bi-x-circle-fill excluded`} data-tooltip-id={`lk-${employee.hash_id}-tooltip`}></i>
              <ReactTooltip id={`lk-${employee.hash_id}-tooltip`} place="bottom" content={translate('tooltips.balance_scorecard.rating_mode.exclude_from_review')} />
            </span>
          }
        </div>
      }
      {
        sortedEmployeeBalancedScorecards.map((bs, j) => <BalancedScorecardFieldValue key={j} employee={employee} bs={bs} field={field} classNames={classNames} forEmployeeManagement={forEmployeeManagement} displayLkTag={displayLkTag} useInterviewIcon={useInterviewIcon} softwareSettings={softwareSettings} resizedCount={resizedCount} useCalculationPayFactor={useCalculationPayFactor} />)
      }
    </td>
  }
}
